import React, { useEffect, useState, useContext } from "react";
import {
  Card,
  Table,
  Tag,
  Form,
  DatePicker,
  Input,
  Select,
  Button,
} from "antd";
import { getOrders } from "../../../network/network";
import { AppContext } from "../../../context/AppContextProvider";
import CurrencyFormatter from "../../../components/CurrencyFormatter";
import {
  transactionTypes,
  UserType,
  transactionOrderSource,
  TransactionOrderSource,
  TransactionStatus,
  AddonName,
} from "../../../configs/constants";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { handleApiError } from "../../../utils/errorHandler";
import { getTableRowNumber } from "../../../utils/tablePagination";

const InprogressOrders = ({ branchesData, isAllData }) => {
  const navigate = useNavigate();
  const { authUser, appConfig } = useContext(AppContext);
  const [isDataLoading, setIsDataLoading] = useState(false);
  const [ordersTableData, setOrdersTableData] = useState([]);
  const [paginationData, setPaginationData] = useState({
    current: 1,
    pageSize: 15,
    total: 0,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [perPageSize, setPerPageSize] = useState(10);
  const [searchParams, setSearchParams] = useState([]);
  const [searchFormRef] = Form.useForm();
  const { t } = useTranslation();
  const { RangePicker } = DatePicker;

  const is_kitchen_display_active = authUser.active_addons.some(
    (addon) => addon.id && addon.name === AddonName.KitchenDisplaySystem,
  );

  useEffect(() => {
    setIsDataLoading(true);
    getOrders({
      ...searchParams,
      page: currentPage,
      per_page: perPageSize,
      status: TransactionStatus.InProgress,
      is_all_data: isAllData,
    })
      .then((response) => {
        if (response?.data?.success === false) {
          handleApiError(response, t);
          return;
        }

        setOrdersTableData(response.data.data.transactions);

        const apiPagination = response.data.data.pagination || {};
        setPaginationData({
          current: apiPagination.current_page || currentPage,
          pageSize: apiPagination.per_page || perPageSize,
          total: apiPagination.total || 0,
        });
      })
      .catch((error) => handleApiError(error, t))
      .finally(() => {
        setIsDataLoading(false);
      });
  }, [searchParams, currentPage, perPageSize, isAllData, t]);

  const firstPartCol = [
    {
      title: "#",
      key: "index",
      className: "app-table-index-column",
      render: (text, record, index) => (
        <>{getTableRowNumber(index, paginationData)}</>
      ),
    },
    {
      title: t("order_id"),
      dataIndex: "order_id",
      render: (order_id) => (order_id ? order_id : "-"),
    },
    {
      title: t("amount"),
      render: (record) => (
        <>
          {record.is_multipayment ? (
            record.multipayments.map((item, i) => (
              <p key={i}>
                {item.transaction_type}: {item.amount}
              </p>
            ))
          ) : (
            <p>
              <CurrencyFormatter value={record.amount_charged} />
            </p>
          )}
        </>
      ),
    },
    {
      title: t("source"),
      render: (record) => (
        <div className="app-flex-wrap-center">
          {Number(record.order_source?.value) ===
            TransactionOrderSource.Pos && (
            <img
              src="/icons/pos.svg"
              alt={t("pos")}
              className="app-order-meta-icon"
            />
          )}

          {Number(record.order_source?.value) ===
            TransactionOrderSource.QrOrder &&
            (record.is_drive_thru ? (
              <img
                src="/icons/car.svg"
                alt={t("drive_thru")}
                className="app-order-meta-icon"
              />
            ) : (
              <img
                src="/icons/qr.svg"
                alt={t("qr")}
                className="app-order-meta-icon"
              />
            ))}

          {Number(record.order_source?.value) ===
            TransactionOrderSource.Kiosk && (
            <img
              src="/icons/kiosk.svg"
              alt={t("kiosk")}
              className="app-order-meta-icon"
            />
          )}

          {record.dining_table_name && (
            <Tag color="blue">{record.dining_table_name}</Tag>
          )}

          {record.order_type?.name && (
            <Tag color="pink">{record.order_type?.name}</Tag>
          )}
        </div>
      ),
    },
  ];

  const branchCol = [
    {
      title: t("branch"),
      dataIndex: "branch",
      render: (branch) => <p>{branch}</p>,
    },
  ];

  const kitchenStatusCol = [
    {
      title: t("kitchen_status"),
      dataIndex: "kitchen_display_status",
      render: (kitchen_display_status) => {
        if (!kitchen_display_status) return "-";

        const statusMap = {
          1: {
            text: t("awaiting"),
            color: "orange",
          },
          2: {
            text: t("preparing"),
            color: "var(--color-info)",
          },
          3: {
            text: t("cooked"),
            color: "var(--color-success)",
          },
        };

        const status = statusMap[kitchen_display_status.value];

        if (!status) return "-";

        return <Tag color={status.color}>{status.text}</Tag>;
      },
    },
  ];

  const secondPartCol = [
    {
      title: `${t("created_at")} / ${t("created_by")}`,
      render: (record) => (
        <>
          <p>{moment(record.created_at).format("MMM. D, YYYY hh:mm a")}</p>
          {record.user && <Tag color="cyan">{record.user}</Tag>}
        </>
      ),
    },
    {
      title: t("action"),
      width: "130px",
      render: (record) => (
        <Button
          type="primary"
          onClick={() => {
            navigate("/orders/" + record.id);
          }}
        >
          {t("preview")}
        </Button>
      ),
    },
  ];
  const columns =
    authUser.type === UserType.BusinessOwner
      ? [
          ...firstPartCol,
          ...(is_kitchen_display_active ? kitchenStatusCol : []),
          ...branchCol,
          ...secondPartCol,
        ]
      : [
          ...firstPartCol,
          ...(is_kitchen_display_active ? kitchenStatusCol : []),
          ...secondPartCol,
        ];

  const handleSearch = (values) => {
    let search_params = false;

    if (values.dates) {
      let startDateUtc = moment.utc(values.dates[0]).set({
        date: values.dates[0].date() - 1,
        month: values.dates[0].month(),
        year: values.dates[0].year(),
        hour: 19,
        minute: 0,
        second: 0,
        millisecond: 0,
      });
      let endDateUtc = moment.utc(values.dates[1]).set({
        date: values.dates[1].date(),
        month: values.dates[1].month(),
        year: values.dates[1].year(),
        hour: 18,
        minute: 59,
        second: 59,
        millisecond: 0,
      });

      search_params = {
        search_start_date: startDateUtc.format(),
        search_end_date: endDateUtc.format(),
      };
    }

    if (values.type) {
      search_params = { type: values.type, ...search_params };
    }

    if (values.order_source) {
      search_params = { order_source: values.order_source, ...search_params };
    }

    if (values.filter_by_branch) {
      search_params = {
        filter_by_branch: values.filter_by_branch,
        ...search_params,
      };
    }

    if (search_params) setSearchParams(search_params);
    else setSearchParams([]);
  };

  const handleReset = () => {
    searchFormRef.resetFields();
    setSearchParams([]);
  };

  const searchForm = (
    <Form
      name="search-form"
      layout="inline"
      onFinish={handleSearch}
      form={searchFormRef}
    >
      <Form.Item name="dates" label={t("date_range")}>
        <RangePicker allowClear format="DD/MM/YYYY" />
      </Form.Item>

      <Form.Item name="filter_by_branch" label={t("branch")}>
        <Select
          allowClear
          className="app-w-100px"
          options={branchesData.map((branch) => ({
            label: branch.name,
            value: branch.name,
          }))}
        />
      </Form.Item>

      <Form.Item name="type" label={t("type")}>
        <Select
          allowClear
          className="app-w-100px"
          options={transactionTypes.map((type) => ({
            label: t(type.translationKey),
            value: type.id,
          }))}
        />
      </Form.Item>

      <Form.Item name="order_source" label={t("source")}>
        <Select
          allowClear
          className="app-w-100px"
          options={transactionOrderSource.map((type) => ({
            label: t(type.translationKey),
            value: type.id,
          }))}
        />
      </Form.Item>

      <Form.Item>
        <Input
          type="submit"
          value={t("search")}
          className="app-search-submit"
        />
      </Form.Item>

      <Form.Item>
        <Input type="button" value={t("reset")} onClick={handleReset} />
      </Form.Item>
    </Form>
  );

  const handleTableChange = (pagination) => {
    setPaginationData({
      current: pagination.current,
      pageSize: pagination.pageSize,
      total: pagination.total,
    });
    setCurrentPage(pagination.current);
    setPerPageSize(pagination.pageSize);
  };

  return (
    <>
      <Card
        style={{
          direction: appConfig.direction
            ? appConfig.direction === "ltr"
              ? "ltr"
              : "rtl"
            : "ltr",
        }}
      >
        {searchForm}
      </Card>

      <Card>
        <Table
          columns={columns}
          dataSource={ordersTableData}
          loading={isDataLoading}
          rowKey="id"
          pagination={{
            current: paginationData.current,
            pageSize: paginationData.pageSize,
            total: paginationData.total,
            showSizeChanger: true,
          }}
          onChange={handleTableChange}
        />
      </Card>
    </>
  );
};

export default InprogressOrders;
